import { useRef } from 'react';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';

import { SWR_GLOBAL_CONFIG } from '../_constant';

type DefineOption<Response> = {
  api: (page: number) => Promise<Response>;
  path: string;
  keys?: unknown[];
  /** 一回のapiで取得する最大件数 */
  lengthPerPage: number;
};

export function usePaginationSWR<Response extends unknown>(
  option: DefineOption<Response[]>,
  swrOption?: SWRInfiniteConfiguration<Response[]>,
) {
  const maxPageRef = useRef<number>();

  /** NOTE:
   * - 第1引数のgetKeyを正しく動作させるために、配列内にobjectではなく値を指定しないといけない
   * - 第1引数のgetKeyは[最後に到達したら`return null`]と↓に書かれているが、挙動がおかしいので`isLoadedAllRef`で代用
   *   -  https://swr.vercel.app/ja/docs/pagination#example-1-index-based-paginated-api
   * - swrのpageは0から始まるので、0の時はskipする (`initialSize: 1`を指定してもなぜか0から)
   * - page=0の時はswrが無駄にfetchするので、`if (page === 0) return [];`で回避する
   */
  const swr = useSWRInfinite(
    (page) => [option.path, page + 1, ...(option.keys ?? [])] as const,
    async ([, page]) => {
      const maybeMaxPage = maxPageRef.current;
      const isDone = typeof maybeMaxPage === 'number' && page > maybeMaxPage;
      if (page === 0 || isDone) return [];
      const nextResponse = await option.api(page);
      if (nextResponse.length < option.lengthPerPage) {
        maxPageRef.current = page;
      }
      return nextResponse;
    },
    { ...SWR_GLOBAL_CONFIG, initialSize: 1, parallel: true, ...swrOption },
  );

  const mutate: typeof swr.mutate = (...args) => {
    maxPageRef.current = undefined;
    return swr.mutate(...args);
  };

  const initializeObserver = usePaginationIntersect(() => {
    const { size: currentPage, setSize } = swr;
    const maybeMaxPage = maxPageRef.current;
    const isDone = typeof maybeMaxPage === 'number' && currentPage > maybeMaxPage;
    if (isDone) return;
    setSize((page) => page + 1);
  });

  return {
    ...swr,
    mutate,
    flattenData: swr.data?.flat(),
    isLoading: swr.isValidating,
    initializeObserver,
    maxPageRef,
  };
}

/**
 * @example
 * ```tsx
 * const initializeObserver = usePaginationIntersect(() => {
 *   // divがintersectした時の処理
 * })
 * <div ref={initializeObserver} />
 * ```
 */
export function usePaginationIntersect(_handleIntersecting: () => void) {
  const handleIntersectingRef = useRef<() => void>();
  handleIntersectingRef.current = _handleIntersecting;

  type Store = {
    // NOTE: 違うHTMLElementをinitするとき(例:タブ切り替えとか)のために
    //       WeakSetを用いてHTMLElementごとにinitしたかを記録してる
    weakSet?: WeakSet<Element>;
    observer?: IntersectionObserver;
  };
  const storeRef = useRef<Store>({});

  const initializeObserver = (el: HTMLElement | null) => {
    storeRef.current.weakSet ??= new WeakSet();
    storeRef.current.observer ??= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          handleIntersectingRef.current?.();
        }
      });
    });

    const { weakSet, observer } = storeRef.current;
    if (!el || weakSet.has(el)) return;
    observer.observe(el);
    weakSet.add(el);
  };

  return initializeObserver;
}
